import axios, { AxiosError, isAxiosError } from "axios";
import userService from "./userService";
import { logoutUser } from "./logoutHelper";
import { getDomain } from "../config/domains";
import type {
    ApiErrorEnvelope,
    ApiSuccessEnvelope,
    AdminNewsDetail,
    AdminNewsInput,
    AdminNewsSummary,
    NewsDetail,
    NewsOverview,
    NewsSort,
    PaginatedNews,
    NewsRating,
    NewsReview,
    ReadingHistoryNews,
    SavedNews,
    UsageEntry,
    User,
} from "../types";

export class ApiError extends Error {
    constructor(
        message: string,
        public readonly code: string,
        public readonly status?: number,
        public readonly details: Record<string, unknown> = {},
        options?: ErrorOptions,
    ) {
        super(message, options);
        this.name = "ApiError";
    }
}

export const api = axios.create({
    baseURL: getDomain("api"),
    headers: {
        "Content-Type": "application/json",
    },
});

export const refreshApi = axios.create({
    baseURL: getDomain("api"),
    headers: {
        "Content-Type": "application/json",
    },
});

const successData = <TData>(response: { data: ApiSuccessEnvelope<TData> }): TData =>
    response.data.success.data;

export const toApiError = (
    error: unknown,
    fallbackMessage = "The request failed.",
): ApiError => {
    if (error instanceof ApiError) {
        return error;
    }

    if (isAxiosError<ApiErrorEnvelope | { message?: string }>(error)) {
        const responseBody = error.response?.data;
        if (
            responseBody &&
            "error" in responseBody &&
            typeof responseBody.error === "object"
        ) {
            return new ApiError(
                responseBody.error.message || fallbackMessage,
                responseBody.error.code || "REQUEST_FAILED",
                error.response?.status,
                responseBody.error.details ?? {},
                { cause: error },
            );
        }

        const legacyMessage =
            responseBody && "message" in responseBody
                ? responseBody.message
                : undefined;

        return new ApiError(
            legacyMessage || fallbackMessage,
            error.response ? "REQUEST_FAILED" : "NETWORK_ERROR",
            error.response?.status,
            {},
            { cause: error },
        );
    }

    return new ApiError(fallbackMessage, "REQUEST_FAILED", undefined, {}, {
        cause: error,
    });
};

export const login = async (userData: {
    username: string;
    password: string;
}): Promise<{ authToken: string; refreshToken: string }> => {
    try {
        const response = await api.post<{
            token: string;
            refresh_token: string;
        }>("/api/auth/login", userData);

        return {
            authToken: response.data.token,
            refreshToken: response.data.refresh_token,
        };
    } catch (error) {
        throw toApiError(error, "Login failed.");
    }
};

export const getCurrentUser = async (): Promise<User> => {
    try {
        const response = await api.get<ApiSuccessEnvelope<{ user: User }>>(
            "/api/user/me",
        );
        return successData(response).user;
    } catch (error) {
        throw toApiError(error, "Failed to load the current user.");
    }
};

export const getAllNews = async (): Promise<NewsOverview[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ news: NewsOverview[] }>
        >("/api/news/all");
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to fetch news.");
    }
};

export const getNews = async (params: {
    page: number;
    pageSize?: number;
    query?: string;
    category?: string;
    sort?: NewsSort;
}): Promise<PaginatedNews> => {
    try {
        const response = await api.get<ApiSuccessEnvelope<PaginatedNews>>(
            "/api/news",
            {
                params: {
                    page: params.page,
                    pageSize: params.pageSize ?? 12,
                    query: params.query || undefined,
                    category: params.category || undefined,
                    sort: params.sort ?? "newest",
                },
            },
        );
        return successData(response);
    } catch (error) {
        throw toApiError(error, "Failed to fetch news.");
    }
};

export const getNewsCategories = async (): Promise<string[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ categories: string[] }>
        >("/api/news/categories");
        return successData(response).categories;
    } catch (error) {
        throw toApiError(error, "Failed to load news categories.");
    }
};

export const getNewsDetail = async (newsId: string): Promise<NewsDetail> => {
    try {
        const response = await api.get<ApiSuccessEnvelope<{ news: NewsDetail }>>(
            `/api/news/${newsId}`,
        );
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to load the article.");
    }
};

export const postComment = async (
    newsId: string,
    comment: string,
): Promise<NewsReview> => {
    try {
        const response = await api.post<
            ApiSuccessEnvelope<{ comment: NewsReview }>
        >(`/api/news/${newsId}/comment`, { comment });
        return successData(response).comment;
    } catch (error) {
        throw toApiError(error, "Failed to post the comment.");
    }
};

export const putRating = async (
    newsId: string,
    rating: number,
): Promise<NewsRating> => {
    try {
        const response = await api.put<
            ApiSuccessEnvelope<{ rating: NewsRating }>
        >(`/api/news/${newsId}/rate`, { rating });
        return successData(response).rating;
    } catch (error) {
        throw toApiError(error, "Failed to submit the rating.");
    }
};

export const getUsage = async (): Promise<UsageEntry[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ usage: UsageEntry[] }>
        >("/api/user/usage");
        return successData(response).usage;
    } catch (error) {
        throw toApiError(error, "Failed to load subscription usage.");
    }
};

export const getBookmarks = async (): Promise<SavedNews[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ items: SavedNews[] }>
        >("/api/user/bookmarks");
        return successData(response).items;
    } catch (error) {
        throw toApiError(error, "Failed to load saved articles.");
    }
};

export const addBookmark = async (newsId: number): Promise<void> => {
    try {
        await api.post(`/api/user/bookmarks/${newsId}`);
    } catch (error) {
        throw toApiError(error, "Failed to save the article.");
    }
};

export const removeBookmark = async (newsId: number): Promise<void> => {
    try {
        await api.delete(`/api/user/bookmarks/${newsId}`);
    } catch (error) {
        throw toApiError(error, "Failed to remove the saved article.");
    }
};

export const getReadingHistory = async (): Promise<ReadingHistoryNews[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ items: ReadingHistoryNews[] }>
        >("/api/user/history");
        return successData(response).items;
    } catch (error) {
        throw toApiError(error, "Failed to load reading history.");
    }
};

export const getAdminNews = async (): Promise<AdminNewsSummary[]> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ items: AdminNewsSummary[] }>
        >("/api/admin/news");
        return successData(response).items;
    } catch (error) {
        throw toApiError(error, "Failed to load editorial articles.");
    }
};

export const getAdminNewsDetail = async (
    newsId: number,
): Promise<AdminNewsDetail> => {
    try {
        const response = await api.get<
            ApiSuccessEnvelope<{ news: AdminNewsDetail }>
        >(`/api/admin/news/${newsId}`);
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to load the editorial article.");
    }
};

export const createAdminNews = async (
    input: AdminNewsInput,
): Promise<AdminNewsDetail> => {
    try {
        const response = await api.post<
            ApiSuccessEnvelope<{ news: AdminNewsDetail }>
        >("/api/admin/news", input);
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to create the article.");
    }
};

export const updateAdminNews = async (
    newsId: number,
    input: AdminNewsInput,
): Promise<AdminNewsDetail> => {
    try {
        const response = await api.put<
            ApiSuccessEnvelope<{ news: AdminNewsDetail }>
        >(`/api/admin/news/${newsId}`, input);
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to update the article.");
    }
};

export const publishAdminNews = async (
    newsId: number,
): Promise<AdminNewsDetail> => {
    try {
        const response = await api.post<
            ApiSuccessEnvelope<{ news: AdminNewsDetail }>
        >(`/api/admin/news/${newsId}/publish`);
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to publish the article.");
    }
};

export const unpublishAdminNews = async (
    newsId: number,
): Promise<AdminNewsDetail> => {
    try {
        const response = await api.post<
            ApiSuccessEnvelope<{ news: AdminNewsDetail }>
        >(`/api/admin/news/${newsId}/unpublish`);
        return successData(response).news;
    } catch (error) {
        throw toApiError(error, "Failed to unpublish the article.");
    }
};

export const archiveAdminNews = async (newsId: number): Promise<void> => {
    try {
        await api.delete(`/api/admin/news/${newsId}`);
    } catch (error) {
        throw toApiError(error, "Failed to archive the article.");
    }
};

export const recordCheckoutTelemetry = async (
    status: "started" | "syncing" | "confirmed" | "pending" | "failed",
    plan?: string,
): Promise<void> => {
    try {
        await api.post("/api/telemetry/checkout", {
            status,
            plan: plan || null,
        });
    } catch {
        // Telemetry must never interrupt the checkout or account experience.
    }
};

api.interceptors.request.use((config) => {
    const token = userService.getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token?: string) => {
    failedQueue.forEach((promise) => {
        if (error || !token) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as
            | (NonNullable<AxiosError["config"]> & { _retry?: boolean })
            | undefined;
        const requestUrl = originalRequest?.url ?? "";
        const isAuthenticationRequest =
            requestUrl.includes("/api/auth/login") ||
            requestUrl.includes("/api/auth/refresh");

        if (
            error.response?.status !== 401 ||
            !originalRequest ||
            originalRequest._retry ||
            isAuthenticationRequest
        ) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = userService.getRefreshToken();
        if (!refreshToken) {
            processQueue(error);
            logoutUser();
            return Promise.reject(error);
        }

        try {
            const response = await refreshApi.post<{
                token: string;
                refresh_token: string;
            }>("/api/auth/refresh", {
                refresh_token: refreshToken,
            });

            const newAuthToken = response.data.token;
            const newRefreshToken =
                response.data.refresh_token || refreshToken;
            userService.login(newAuthToken, newRefreshToken);
            processQueue(undefined, newAuthToken);

            originalRequest.headers.Authorization = `Bearer ${newAuthToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError);
            logoutUser();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    },
);

export default api;
